<!-- System events sit centred with no bubble: "Sarah added Michael", "renamed the group", … -->
<div class="chat-system" *ngIf="isSystem; else bubbleTpl">
  <span>{{ systemText }}</span>
</div>

<ng-template #bubbleTpl>
  <div class="chat-msg" [class.is-own]="isOwn" [class.is-grouped]="grouped">
    <!--
      Avatar column: printed once per block, but the space is always reserved so bubbles stay
      aligned. Your own messages sit on the right and everyone else's on the left — the WhatsApp
      layout — which is done entirely by mirroring this row in CSS, so there is one DOM order to
      reason about rather than two.
    -->
    <div class="chat-msg__avatar">
      <app-chat-avatar *ngIf="!grouped" [name]="message.sender.name" [avatar]="message.sender.avatar_url" [size]="32"></app-chat-avatar>
    </div>

    <div class="chat-msg__main">
      <!-- Name and time on every block. On your own messages the name is redundant with the side
           it is on, so only the time is kept. -->
      <div class="chat-msg__meta" *ngIf="!grouped">
        <span class="chat-msg__sender" *ngIf="!isOwn">{{ message.sender.name }}</span>
        <span class="chat-msg__time">{{ timeLabel(message.created_at) }}</span>
      </div>

      <div class="chat-msg__row">
        <div class="chat-msg__bubble" [class.is-own]="isOwn" [class.is-deleted]="message.is_deleted" [class.is-failed]="message.failed">
          <!-- Deleted tombstone: kept in place so pagination and "seen" positions stay stable -->
          <p class="chat-msg__deleted" *ngIf="message.is_deleted">This message was deleted</p>

          <ng-container *ngIf="!message.is_deleted">
            <!-- Inline edit -->
            <div class="chat-msg__edit" *ngIf="editing; else bodyTpl">
              <textarea #editBox [(ngModel)]="draft" rows="2" (keydown)="onEditKeydown($event)" aria-label="Edit message"></textarea>
              <div class="chat-msg__edit-actions">
                <button type="button" class="chat-msg__edit-cancel" (click)="cancelEdit()">Cancel</button>
                <button type="button" class="chat-msg__edit-save" (click)="saveEdit()">Save</button>
              </div>
              <small>Enter to save · Esc to cancel</small>
            </div>

            <ng-template #bodyTpl>
              <!--
                A message carrying a call link renders as a call card instead of a wall of URL.
                Driven by the link itself rather than a message type, so a Zoom or Teams link
                somebody pastes by hand gets the same treatment — see `chat-call-link.util.ts`.
              -->
              <div class="chat-call" *ngIf="callLink as link; else plainBodyTpl">
                <div class="chat-call__head">
                  <span class="chat-call__icon"><i class="pi pi-video"></i></span>
                  <div class="chat-call__body">
                    <span class="chat-call__title">{{ callTitle }}</span>
                    <span class="chat-call__code">{{ callLabel }}</span>
                  </div>
                  <a class="chat-call__join" [href]="link.url" target="_blank" rel="noopener noreferrer" (click)="onJoinCall()">Join</a>
                </div>

                <!--
                  What this browser saw happen. Only ever on someone else's call, and only ever
                  about the reader — Meet never says who joined, so it cannot speak for anyone else.
                -->
                <span class="chat-call__outcome" *ngIf="callOutcomeLabel" [class.is-missed]="callOutcome === 'missed'">
                  <i class="pi" [ngClass]="callOutcome === 'joined' ? 'pi-check' : 'pi-times'"></i>
                  {{ callOutcomeLabel }}
                </span>

                <!-- Secondary actions: useful to whoever *received* the link, so they sit on every card -->
                <div class="chat-call__actions">
                  <button type="button" (click)="copyCallLink()" title="Copy call link" aria-label="Copy call link">
                    <i class="pi pi-copy"></i> Copy link
                  </button>
                  <button type="button" (click)="addCallToCalendar()" title="Add to calendar" aria-label="Add call to calendar">
                    <i class="pi pi-calendar-plus"></i> Add to calendar
                  </button>
                </div>
              </div>

              <!--
                Rendered as segments rather than `[innerHTML]`: a body is whatever a colleague
                typed, so building HTML from it would put user input one sanitiser bug away from
                script execution. Kept on one line because the bubble is `white-space: pre-wrap`
                and any newline in this template would show up inside the message.
              -->
              <ng-template #plainBodyTpl>
                <!-- prettier-ignore -->
                <p class="chat-msg__body" *ngIf="message.body"><ng-container *ngFor="let segment of bodySegments; trackBy: trackBySegment"><a *ngIf="segment.href; else plainSegmentTpl" class="chat-msg__link" [href]="segment.href" target="_blank" rel="noopener noreferrer" (click)="$event.stopPropagation()">{{ segment.text }}</a><ng-template #plainSegmentTpl>{{ segment.text }}</ng-template></ng-container></p>
              </ng-template>

              <div class="chat-msg__attachments" *ngIf="message.attachments?.length">
                <ng-container *ngFor="let attachment of message.attachments; trackBy: trackByAttachment">
                  <!-- Image: thumbnail, opens full size in a new tab -->
                  <button type="button" class="chat-att chat-att--image" *ngIf="attachment.kind === 'image'" (click)="openAttachment(attachment)">
                    <img [src]="attachment.url" [alt]="attachment.name" (error)="onImageError(attachment)" />
                  </button>

                  <!-- Voice note: the browser's own player, no custom transport -->
                  <div class="chat-att chat-att--voice" *ngIf="attachment.kind === 'voice'">
                    <audio controls preload="metadata" [src]="attachmentUrl(attachment)" (error)="onImageError(attachment)"></audio>
                    <span class="chat-att__duration" *ngIf="attachment.duration_ms">{{ durationLabel(attachment.duration_ms) }}</span>
                  </div>

                  <!-- Everything else: name, size, download -->
                  <a
                    class="chat-att chat-att--file"
                    *ngIf="attachment.kind === 'file'"
                    [href]="downloadHref(attachment)"
                    [attr.download]="attachment.name"
                    target="_blank"
                    rel="noopener">
                    <span class="chat-att__icon"><i class="pi pi-file"></i></span>
                    <span class="chat-att__meta">
                      <span class="chat-att__name">{{ attachment.name }}</span>
                      <span class="chat-att__size">{{ formatFileSize(attachment.size) }}</span>
                    </span>
                    <i class="pi pi-download"></i>
                  </a>
                </ng-container>
              </div>
            </ng-template>
          </ng-container>

          <span class="chat-msg__edited" *ngIf="message.edited_at && !message.is_deleted">(edited)</span>
        </div>

        <!--
          Hover toolbar. Floats over the top corner of the bubble instead of sitting beside it,
          so it costs the message no width — the reason bubbles can now run wider.
        -->
        <div class="chat-msg__actions" *ngIf="!message.is_deleted && !editing" [class.is-open]="reactionPickerOpen || menuOpen">
          <button type="button" class="chat-msg__action" (click)="toggleReactionPicker()" title="React" aria-label="React">
            <i class="pi pi-face-smile"></i>
          </button>
          <!-- Forward is offered on anyone's message, so the menu now opens on far more of them
               than when it only held Edit and Delete. -->
          <button
            type="button"
            class="chat-msg__action"
            *ngIf="canEdit || canDelete || canForward"
            (click)="toggleMenu()"
            title="More"
            aria-label="More actions">
            <i class="pi pi-ellipsis-h"></i>
          </button>

          <!--
            Each popover hangs off the button that opens it — the picker above the emoji icon on
            the left, the menu below the dots on the right. `popoverShift` nudges either one back
            inside the transcript when the message sits too close to an edge for it to fit.
          -->
          <div #picker class="chat-msg__picker" *ngIf="reactionPickerOpen" [style.transform]="shiftStyle">
            <button type="button" *ngFor="let emoji of quickReactions" (click)="react(emoji)" [attr.aria-label]="'React with ' + emoji">
              {{ emoji }}
            </button>
          </div>

          <div #menu class="chat-msg__menu" *ngIf="menuOpen" [style.transform]="shiftStyle">
            <button type="button" *ngIf="canForward" (click)="forwardMessage()"><i class="pi pi-share-alt"></i> Forward</button>
            <button type="button" *ngIf="canEdit" (click)="startEdit()"><i class="pi pi-pencil"></i> Edit</button>
            <button type="button" class="is-danger" *ngIf="canDelete" (click)="remove()"><i class="pi pi-trash"></i> Delete</button>
          </div>
        </div>
      </div>

      <!-- Existing reactions -->
      <div class="chat-msg__reactions" *ngIf="message.reactions?.length">
        <button
          type="button"
          class="chat-reaction"
          *ngFor="let reaction of message.reactions; trackBy: trackByReaction"
          [class.is-mine]="reaction.reacted_by_me"
          [attr.aria-label]="reactionTooltip(reaction)"
          (click)="react(reaction.emoji)">
          <span>{{ reaction.emoji }}</span>
          <span class="chat-reaction__count">{{ reaction.count }}</span>

          <!--
            Who reacted, on hover — the Slack/WhatsApp affordance. A styled element rather than a
            `title`, which no browser lets you position and which takes a second to appear; the
            same string is on `aria-label` above so it is not hover-only for a screen reader.
          -->
          <span class="chat-reaction__tip" role="tooltip">{{ reactionTooltip(reaction) }}</span>
        </button>
      </div>

      <!--
        Delivery state for your own messages.

        One tick means the server has it; two mean somebody has read it, and they turn primary
        once *everyone* has — which in a DM is simply "they've seen it". There is no per-device
        delivery signal in the protocol, so nothing here claims one.
      -->
      <div class="chat-msg__status" *ngIf="isOwn">
        <span *ngIf="message.pending" class="chat-msg__pending"><i class="pi pi-clock"></i> Sending…</span>
        <span *ngIf="message.failed" class="chat-msg__failed">
          Not sent
          <button type="button" (click)="retry()">Retry</button>
        </span>

        <span
          *ngIf="!message.pending && !message.failed"
          class="chat-msg__seen"
          [class.is-read]="deliveryState === 'read-all'"
          [attr.title]="deliveryLabel"
          [attr.aria-label]="deliveryLabel">
          <span class="chat-msg__seen-text" *ngIf="seenLabel">{{ seenLabel }}</span>

          <!-- Two checks offset by a few pixels: the classic read receipt, not two icons in a row -->
          <svg class="chat-tick" viewBox="0 0 16 12" aria-hidden="true" *ngIf="showDoubleTick; else singleTickTpl">
            <path d="M1 6.1 L4.1 9.3 L9.4 2.4" />
            <path d="M6.3 6.1 L9.4 9.3 L14.7 2.4" />
          </svg>
          <ng-template #singleTickTpl>
            <svg class="chat-tick" viewBox="0 0 16 12" aria-hidden="true">
              <path d="M2.5 6.1 L6.3 9.9 L13.5 2.2" />
            </svg>
          </ng-template>
        </span>
      </div>
    </div>
  </div>
</ng-template>
